"use client";

import { IconBrandX, IconMail } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type { ElementType } from "react";
import { CopyButton } from "@/components/ui/CopyButton";
import { useProjectConfigs } from "@/features/project/ProjectContext";

/**
 * Open a share dialog centred on the user's screen.
 *
 * A sized popup rather than a tab: these are single-purpose dialogs the user
 * dismisses immediately, and a background tab loses that framing.
 */
function openCenteredPopup(
  url: string,
  title: string,
  width: number,
  height: number
) {
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  window.open(
    url,
    title,
    `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
  );
}

function ShareButton({
  label,
  Icon,
  url,
  windowWidth = 600,
  windowHeight = 400,
}: {
  label: string;
  Icon: ElementType;
  url: string;
  windowWidth?: number;
  windowHeight?: number;
}) {
  return (
    <a
      href={url}
      className="mr-2 flex h-8 w-8 items-center justify-center rounded-full bg-primary-opacity-25 p-2 dynamic-text hover:bg-primary"
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      onClick={() =>
        // A mailto has to hand off to the OS handler; popping a window for it
        // leaves an empty one behind.
        !url.startsWith("mailto:") &&
        openCenteredPopup(url, label, windowWidth, windowHeight)
      }
    >
      <Icon
        className="text-sm text-black dark:text-off-white"
        aria-hidden="true"
      />
    </a>
  );
}

/** Build a query string, dropping params with no value. */
function toQueryParams(
  object: Record<string, string | number | undefined | null>
): string {
  const params = Object.entries(object)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    );

  return params.length > 0 ? `?${params.join("&")}` : "";
}

/**
 * The share destinations the tenant has enabled, plus the link itself.
 *
 * Plain links to each network's share endpoint rather than their SDKs, which load
 * third-party script and set cookies on every page view for a button most visitors
 * never press.
 */
export function ShareButtons({ url }: { url: string }) {
  const t = useTranslations();
  const { socialShareButtons } = useProjectConfigs();

  const shareText = t("modal.share.shareText");
  const enabled = socialShareButtons ?? [];

  const mailTo = `mailto:${toQueryParams({ body: `${shareText} ${url}` })}`;
  const xIntent = `https://x.com/intent/tweet${toQueryParams({
    text: shareText,
    url,
  })}`;
  const linkedIn = `https://linkedin.com/shareArticle${toQueryParams({
    mini: "true",
    url,
    source: "Mappr",
    title: shareText,
    summary: `${shareText} ${url}`,
  })}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php${toQueryParams({
    u: url,
  })}`;

  return (
    <div>
      {enabled.length > 0 && (
        <div className="mb-2 mt-3.5 flex">
          {enabled.includes("email") && (
            <ShareButton label="Email" Icon={IconMail} url={mailTo} />
          )}
          {enabled.includes("facebook") && (
            <ShareButton
              label="Facebook"
              Icon={() => (
                <i className="icon ic-jammaster_facebook text-sm text-black dark:text-off-white" />
              )}
              url={facebook}
              windowWidth={680}
              windowHeight={600}
            />
          )}
          {enabled.includes("linkedin") && (
            <ShareButton
              label="LinkedIn"
              Icon={() => (
                <i className="icon ic-jammaster_linkedin text-sm text-black dark:text-off-white" />
              )}
              url={linkedIn}
              windowWidth={750}
              windowHeight={600}
            />
          )}
          {enabled.includes("twitter") && (
            <ShareButton
              label="X"
              Icon={IconBrandX}
              url={xIntent}
              windowWidth={550}
              windowHeight={400}
            />
          )}
        </div>
      )}

      <div className="relative mt-3.5 pb-2">
        <span className="mr-2 block min-h-6 whitespace-normal break-words text-xs text-black dark:text-off-white">
          {url}
        </span>
        <div className="flex justify-end">
          <CopyButton content={url} />
        </div>
      </div>
    </div>
  );
}
